import React, { useEffect, useContext, useState, useCallback } from "react";
import { AppStateContext } from "../../../state/AppProvider";
import { useMsal } from "@azure/msal-react";
import { deleteCustomAssistant, assistantsList, getUserId, leaveAssistant, historyCount, historyList, disableAssistant } from "../../../api/api";
import { Assistant, ChatHistoryLoadingState } from "../../../api/models";
import { getToken } from "../../../authConfig";
import { AssistantEditor } from "..";
import { AssistantViewer } from "./AssistantViewer";
import { AssistantTable } from "../ui/AssistantTable";
import { ConfirmationDialog } from "../ui/ConfirmationDialog";

interface AssistantManagerProps {
    onCancel: () => void;
}

export const AssistantManager = ({ onCancel }: AssistantManagerProps) => {
    const appStateContext = useContext(AppStateContext);
    const { instance: msalInstance } = useMsal();

    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [leaveId, setLeaveId] = useState<string | null>(null);
    const [confirmText, setConfirmText] = useState("");
    const [isDeletingAssistant, setIsDeletingAssistant] = useState(false);
    const [isLeavingAssistant, setIsLeavingAssistant] = useState(false);

    const [myAssistants, setMyAssistants] = useState<Assistant[]>([]);
    const [sharedAssistants, setSharedAssistants] = useState<Assistant[]>([]);
    const [equiomAssistants, setEquiomAssistants] = useState<Assistant[]>([]);

    const [isEditing, setIsEditing] = useState(false);
    const [isViewing, setIsViewing] = useState(false);
    const [isAdminMode, setIsAdminMode] = useState(false);
    const [isFileManagerMode, setIsFileManagerMode] = useState(false);
    const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(null);
    const user_groups = appStateContext?.state.userAuthClaims?.groups || [];
    const [currentUserId, setCurrentUserId] = useState<string>("");

    useEffect(() => {
        console.log("User Auth Claims:", appStateContext?.state.userAuthClaims);
        console.log("Groups:", appStateContext?.state.userAuthClaims?.groups);
    }, [appStateContext?.state.userAuthClaims]);

    useEffect(() => {
        const fetchAssistants = () => {
            setMyAssistants(appStateContext?.state.customAssistants || []);
            setSharedAssistants(appStateContext?.state.sharedAssistants || []);
            setEquiomAssistants(appStateContext?.state.equiomAssistants || []);
        };
        fetchAssistants();
    }, [appStateContext]);

    useEffect(() => {
        const fetchUserId = async () => {
            try {
                const token = msalInstance ? await getToken(msalInstance) : undefined;
                const userIdResponse = await getUserId(token?.accessToken);
                setCurrentUserId(userIdResponse.userId);
            } catch (err) {
                console.error("Error fetching user ID:", err);
            }
        };
        fetchUserId();
    }, [msalInstance]);

    const combinedSharedAssistants = [...equiomAssistants.map(a => ({ ...a, isEquiom: true })), ...sharedAssistants.map(a => ({ ...a, isEquiom: false }))];

    // Refresh list after deleting/leaving
    const refreshAssistantsList = async () => {
        try {
            const token = msalInstance ? await getToken(msalInstance) : undefined;
            const userIdResponse = await getUserId(token?.accessToken);
            const userId = userIdResponse.userId;
            setCurrentUserId(userId);

            const response = await assistantsList(token?.accessToken);
            if (response) {
                appStateContext?.dispatch({ type: "FETCH_ASSISTANTS", payload: response });
                appStateContext?.dispatch({
                    type: "UPDATE_CUSTOM_ASSISTANTS",
                    payload: response.filter(assist => assist.created_by === userId && assist.type === "custom")
                });
                appStateContext?.dispatch({
                    type: "UPDATE_SHARED_ASSISTANTS",
                    payload: response.filter(assist => assist.created_by !== userId && assist.type === "custom")
                });
                appStateContext?.dispatch({
                    type: "UPDATE_ASSISTANT",
                    payload: response.filter(assistant => assistant.assistant === "hr")[0]
                });
            } else {
                appStateContext?.dispatch({ type: "FETCH_ASSISTANTS", payload: null });
            }
        } catch (err) {
            console.error("There was an issue fetching your assistants.", err);
            appStateContext?.dispatch({ type: "FETCH_ASSISTANTS", payload: null });
        }
    };

    const refreshHistoryList = async (msalInstance: any, appStateContext: any): Promise<void> => {
        try {
            const token = msalInstance ? await getToken(msalInstance) : undefined;

            const count = await historyCount(token?.accessToken);
            if (count) {
                appStateContext.dispatch({ type: "SET_HISTORY_COUNT", payload: count });
            } else {
                appStateContext.dispatch({ type: "SET_HISTORY_COUNT", payload: 0 });
            }

            const chatHistory = await historyList(token?.accessToken);
            if (chatHistory) {
                appStateContext.dispatch({ type: "FETCH_CHAT_HISTORY", payload: chatHistory });
                appStateContext.dispatch({
                    type: "SET_HISTORY_CURRENT_COUNT",
                    payload: chatHistory.length
                });
            } else {
                appStateContext.dispatch({ type: "FETCH_CHAT_HISTORY", payload: null });
            }
        } catch (error) {
            console.error("There was an issue fetching chat history: ", error);

            // Update state to reflect failure
            appStateContext.dispatch({
                type: "UPDATE_CHAT_HISTORY_LOADING_STATE",
                payload: ChatHistoryLoadingState.Fail
            });
            appStateContext.dispatch({ type: "FETCH_CHAT_HISTORY", payload: null });
        }
    };

    const confirmDelete = async () => {
        if (confirmText !== "DELETE" || !deleteId) return;
        setIsDeletingAssistant(true);
        appStateContext?.dispatch({
            type: "UPDATE_ASSISTANT",
            payload: equiomAssistants.filter(assistant => assistant.assistant === "hr")[0]
        });
        const token = msalInstance ? await getToken(msalInstance) : undefined;
        try {
            await disableAssistant(deleteId, token?.accessToken);
            await deleteCustomAssistant(deleteId, token?.accessToken);
            await refreshHistoryList(msalInstance, appStateContext);
            await refreshAssistantsList();
        } catch (err) {
            console.error(`Error deleting assistant with id ${deleteId}.`, err);
        } finally {
            setIsDeletingAssistant(false);
            setDeleteId(null);
            setConfirmText("");
        }
    };

    const confirmLeave = async () => {
        if (confirmText !== "LEAVE" || !leaveId) return;
        setIsLeavingAssistant(true);
        appStateContext?.dispatch({
            type: "UPDATE_ASSISTANT",
            payload: equiomAssistants.filter(assistant => assistant.assistant === "hr")[0]
        });
        const token = msalInstance ? await getToken(msalInstance) : undefined;
        try {
            const response = await leaveAssistant(leaveId, token?.accessToken);
            if (!response.ok) console.error("Error leaving assistant:", await response.text());
            else {
                await refreshHistoryList(msalInstance, appStateContext);
                await refreshAssistantsList();
            }
        } catch (err) {
            console.error(`Error leaving assistant with id ${leaveId}`, err);
        } finally {
            setIsLeavingAssistant(false);
            setLeaveId(null);
            setConfirmText("");
        }
    };

    if (isViewing && selectedAssistantId)
        return (
            <AssistantViewer
                id={selectedAssistantId}
                onCancel={() => {
                    setIsViewing(false);
                    setSelectedAssistantId(null);
                }}
            />
        );

    if (isEditing && selectedAssistantId)
        return (
            <AssistantEditor
                id={selectedAssistantId}
                onSubmit={() => {
                    setIsEditing(false);
                    setSelectedAssistantId(null);
                    refreshAssistantsList();
                }}
                onCancel={() => {
                    setIsEditing(false);
                    setSelectedAssistantId(null);
                }}
                isAdminMode={isAdminMode}
                isFileManagerMode={isFileManagerMode}
            />
        );

    if (isAdminMode && selectedAssistantId)
        return (
            <AssistantEditor
                id={selectedAssistantId}
                onSubmit={() => {
                    setIsEditing(false);
                    setSelectedAssistantId(null);
                    refreshAssistantsList();
                }}
                onCancel={() => {
                    setIsAdminMode(false);
                    setSelectedAssistantId(null);
                }}
                isAdminMode={isAdminMode}
                isFileManagerMode={isFileManagerMode}
            />
        );

    if (isFileManagerMode && selectedAssistantId)
        return (
            <AssistantEditor
                id={selectedAssistantId}
                onSubmit={() => {
                    setIsFileManagerMode(false);
                    setSelectedAssistantId(null);
                    refreshAssistantsList();
                }}
                onCancel={() => {
                    setIsFileManagerMode(false);
                    setSelectedAssistantId(null);
                }}
                isAdminMode={false}
                isFileManagerMode={isFileManagerMode}
            />
        );

    const isUserDataLoading = !currentUserId || appStateContext?.state.userAuthClaims === undefined;

    return (
        <div className="relative flex flex-col h-full">
            {/* Header Section */}
            <div className="sticky top-0 z-10 bg-white px-4 pt-6 pb-6 sm:px-6 border-b border-gray-200">
                <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-purple-100">
                        <span className="material-symbols-outlined text-[1.5rem] text-eq-green">settings</span>
                    </div>
                    {/* Title & Description */}
                    <div>
                        <h3 className="text-lg font-semibold leading-6 text-gray-900">Manage Assistants</h3>
                        <p className="mt-1 text-sm text-gray-500">View, edit, and manage your custom assistants and those shared with you.</p>
                    </div>
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-grow px-8 py-6">
                <h4 className="text-lg font-semibold text-eq-grey mb-4">My Custom Assistants</h4>
                {myAssistants.length ? (
                    <AssistantTable
                        userGroups={user_groups}
                        data={myAssistants}
                        isLoading={isUserDataLoading}
                        onFileManager={id => {
                            setSelectedAssistantId(id);
                            setIsFileManagerMode(true);
                        }}
                        onDelete={id => {
                            setDeleteId(id);
                            setConfirmText("");
                        }}
                        onAdmin={id => {
                            setSelectedAssistantId(id);
                            setIsAdminMode(true);
                        }}
                        currentUserId={currentUserId}
                    />
                ) : (
                    <div className="p-6 rounded-md border-dashed border-gray-200 border text-center">
                        <p className="text-gray-500 text-sm">No assistants found, start by creating one!</p>
                    </div>
                )}

                <div className="mt-8 mb-6">
                    <h4 className="text-lg font-semibold text-eq-grey mb-4">Assistants Shared With Me</h4>
                    {combinedSharedAssistants.length ? (
                        <AssistantTable
                            data={combinedSharedAssistants}
                            isShared
                            userGroups={user_groups}
                            isLoading={isUserDataLoading}
                            onView={id => {
                                setSelectedAssistantId(id);
                                setIsViewing(true);
                            }}
                            onLeave={id => {
                                setLeaveId(id);
                                setConfirmText("");
                            }}
                            onFileManager={id => {
                                setSelectedAssistantId(id);
                                setIsFileManagerMode(true);
                            }}
                            onAdmin={id => {
                                setSelectedAssistantId(id);
                                setIsAdminMode(true);
                            }}
                            currentUserId={currentUserId}
                        />
                    ) : (
                        <div className="p-6 rounded-md border-dashed border-gray-200 border text-center">
                            <p className="text-gray-500 text-sm">No assistants have been shared with you.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 z-10 border-t border-gray-200 bg-white flex flex-row gap-4 px-4 pb-6 pt-4 sm:flex-row-reverse sm:px-6">
                <button className="eq-secondary-button hover:bg-eq-light-grey" onClick={onCancel}>
                    Close
                </button>
            </div>

            <ConfirmationDialog
                open={!!deleteId}
                title="Confirm Deletion"
                body={
                    <>
                        This action is permanent and cannot be undone. All conversations with this assistant will also be lost.
                        <br />
                        Type <strong>DELETE</strong> to proceed.
                    </>
                }
                confirmWord="DELETE"
                busy={isDeletingAssistant}
                onConfirm={confirmDelete}
                onClose={() => setDeleteId(null)}
                confirmText={confirmText}
                setConfirmText={setConfirmText}
            />

            <ConfirmationDialog
                open={!!leaveId}
                title="Confirm Leaving"
                body={
                    <>
                        Are you sure you want to leave this assistant? You will lose access to it and your conversations. The owner can still re-add you at a
                        future date.
                        <br />
                        Type <strong>LEAVE</strong> to proceed.
                    </>
                }
                confirmWord="LEAVE"
                busy={isLeavingAssistant}
                onConfirm={confirmLeave}
                onClose={() => setLeaveId(null)}
                confirmText={confirmText}
                setConfirmText={setConfirmText}
            />
        </div>
    );
};
